import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AssetCategoriesService, AssetCategory } from '../../services/asset-categories.service';

export interface AssetCategoryDialogData {
  title: string;
  assetCategory?: AssetCategory;
}

@Component({
  selector: 'app-asset-category-dialog',
  templateUrl: './asset-category-dialog.component.html',
  styleUrls: ['./asset-category-dialog.component.css']
})
export class AssetCategoryDialogComponent implements OnInit {
  assetCategoryForm: FormGroup;
  isLoading = false;
  isEdit = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AssetCategoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssetCategoryDialogData,
    private assetCategoriesService: AssetCategoriesService,
    private snackBar: MatSnackBar
  ) {
    this.assetCategoryForm = this.createForm();
    this.isEdit = !!data.assetCategory;
  }

  ngOnInit(): void {
    if (this.data.assetCategory) {
      this.assetCategoryForm.patchValue(this.data.assetCategory);
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      id: [null],
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(500)]],
      depreciationRate: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      usefulLife: [1, [Validators.required, Validators.min(1), Validators.max(50)]]
    });
  }

  get name() { return this.assetCategoryForm.get('name'); }
  get description() { return this.assetCategoryForm.get('description'); }
  get depreciationRate() { return this.assetCategoryForm.get('depreciationRate'); }
  get usefulLife() { return this.assetCategoryForm.get('usefulLife'); }

  onSubmit(): void {
    if (this.assetCategoryForm.invalid) {
      this.markFormGroupTouched(this.assetCategoryForm);
      this.snackBar.open('Please fill in all required fields correctly', 'Close', { duration: 3000 });
      return;
    }

    this.isLoading = true;
    const assetCategory: AssetCategory = this.assetCategoryForm.value;

    if (this.isEdit) {
      this.updateAssetCategory(assetCategory);
    } else {
      this.createAssetCategory(assetCategory);
    }
  }

  createAssetCategory(assetCategory: AssetCategory): void {
    this.assetCategoriesService.createAssetCategory(assetCategory).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open('Asset category created successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(response.data);
        } else {
          this.snackBar.open(response.message || 'Failed to create asset category', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error creating asset category:', error);
        this.snackBar.open('Error creating asset category', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  updateAssetCategory(assetCategory: AssetCategory): void {
    if (!assetCategory.id) return;

    this.assetCategoriesService.updateAssetCategory(assetCategory.id, assetCategory).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open('Asset category updated successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(response.data);
        } else {
          this.snackBar.open(response.message || 'Failed to update asset category', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error updating asset category:', error);
        this.snackBar.open('Error updating asset category', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getErrorMessage(field: any): string {
    if (field?.errors) {
      if (field.errors['required']) {
        return 'This field is required';
      }
      if (field.errors['minlength']) {
        return `Minimum length is ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors['maxlength']) {
        return `Maximum length is ${field.errors['maxlength'].requiredLength} characters`;
      }
      if (field.errors['min']) {
        return `Minimum value is ${field.errors['min'].min}`;
      }
      if (field.errors['max']) {
        return `Maximum value is ${field.errors['max'].max}`;
      }
    }
    return '';
  }

  calculateAnnualDepreciation(): number {
    // This is a placeholder calculation - in real implementation, you might calculate based on purchase value
    // For now, we'll just show the depreciation rate as a percentage indicator
    const depreciationRate = this.assetCategoryForm.get('depreciationRate')?.value || 0;
    return depreciationRate; // This will be displayed as currency in the template
  }
}
